import type { CSSProperties } from "react";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useIsMember } from "../lib/permissions";
import { AppSidebar } from "./app-sidebar";
import { CommandMenuProvider } from "./command-menu";
import { MobileTabBar } from "./mobile-tab-bar";
import { SiteHeader } from "./site-header";
import { SidebarInset, SidebarProvider } from "./ui/sidebar";

export function DashboardLayout() {
  useNewIssueShortcut();
  return (
    <CommandMenuProvider>
      <SidebarProvider style={{ "--sidebar-width": "16rem" } as CSSProperties}>
        <AppSidebar variant="inset" />
        <SidebarInset className="min-w-0 pb-(--tab-bar-h)">
          <SiteHeader />
          {/*
           * Every page element starts on the gutter; the extra bottom room keeps the last rows above
           * floating bars. `--sticky-top` is where sticky columns rest, just under the header.
           */}
          <div className="@container/main fx-rise flex flex-1 flex-col gap-6 px-gutter pt-5 pb-16 [--sticky-top:4rem] md:pt-7">
            <Outlet />
          </div>
        </SidebarInset>
        <MobileTabBar />
      </SidebarProvider>
    </CommandMenuProvider>
  );
}

/** `C` opens a blank issue from anywhere, unless the user is already typing. Users and admins. */
function useNewIssueShortcut() {
  const navigate = useNavigate();
  const location = useLocation();
  const member = useIsMember();
  useEffect(() => {
    if (!member) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true]")) return;
      if (event.key.toLowerCase() !== "c" || event.metaKey || event.ctrlKey || event.altKey || event.repeat)
        return;
      if (location.pathname === "/issues/new") return;
      event.preventDefault();
      navigate("/issues/new");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [member, location.pathname, navigate]);
}
